import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

/**
 * Deduplicates identical GET requests and caches responses.
 * Uses shareReplay with sliding expiration - each cache hit restarts the timer.
 * Only applies to whitelisted URL patterns.
 */
@Injectable()
export class DeduplicationInterceptor implements HttpInterceptor {
    private cache = new Map<string, Observable<HttpEvent<unknown>>>();
    private timeouts = new Map<string, ReturnType<typeof setTimeout>>();
    private readonly CACHE_TIME_MS = 10_000;

    private readonly CACHE_URL_PATTERNS: RegExp[] = [
        /\/purchase\/product\/channel\//,
        /\/purchase\/filter\/(areas|categories|period|fare-zones)\/channel\//,
        /\/ticket\/recent/,
        /\/afc-customers\/customer-accounts\/channel\//
    ];

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (request.method !== 'GET' || !this.shouldCache(request.url)) {
            return next.handle(request);
        }

        const cacheKey = this.getCacheKey(request);

        const cachedObservable = this.cache.get(cacheKey);
        if (cachedObservable) {
            return cachedObservable;
        }

        const shared$ = next.handle(request).pipe(
            tap({
                error: () => {
                    this.cache.delete(cacheKey);
                }
            }),
            finalize(() => {
                this.resetTimeout(cacheKey);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        this.cache.set(cacheKey, shared$);

        return shared$;
    }

    private resetTimeout(cacheKey: string): void {
        const existingTimeout = this.timeouts.get(cacheKey);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        const timeout = setTimeout(() => {
            this.cache.delete(cacheKey);
            this.timeouts.delete(cacheKey);
        }, this.CACHE_TIME_MS);

        this.timeouts.set(cacheKey, timeout);
    }

    private shouldCache(url: string): boolean {
        return this.CACHE_URL_PATTERNS.some((pattern) => pattern.test(url));
    }

    private getCacheKey(request: HttpRequest<unknown>): string {
        return request.urlWithParams;
    }
}
